import Helpers from './Helpers.js';
import Objects from './util/Objects.js';

/**
 * @module Core/helper/ObjectHelper
 */

const
    EMPTY_ARRAY = Object.freeze([]),
    { hasOwn } = Objects,
    firstOfAsync = async(iter, defaultValue) => {
        let ret = defaultValue;

        // noinspection LoopStatementThatDoesntLoopJS
        for await (ret of iter) {   // eslint-disable-line no-unreachable-loop
            break;
        }

        return ret;
    },
    toFixedFix = (1.005).toFixed(2) === '1.01' ? null : (number, fractionDigits) => {
        // Browser has bad implementation of toFixed()
        const
            split = number.toString().split('.'),
            newNumber = +(!split[1] ? split[0] : split.join('.') + '1');

        return number.toFixed.call(newNumber, fractionDigits);
    },
    wrapRecursive = (obj, onChange, processed, path = []) => {
        // Do not wrap falsy or things which are not pure raw objects (like Dates, RegExps, etc.)
        if (!obj || !(Objects.isObject(obj) || Array.isArray(obj))) {
            return obj;
        }

        // If we have already processed this object, return the wrapped version
        if (processed.has(obj)) {
            return processed.get(obj);
        }

        const proxy = new Proxy(obj, {
            get(target, prop, receiver) {
                return wrapRecursive(Reflect.get(target, prop, receiver), onChange, processed, path.concat(prop));
            },
            set(target, prop, value, receiver) {
                const
                    oldValue     = target[prop],
                    success      = Reflect.set(target, prop, wrapRecursive(value, onChange, processed, path.concat(prop)), receiver);

                if (success && oldValue !== value) {
                    onChange({
                        oldValue,
                        path     : path.concat(prop),
                        newValue : value
                    });
                }
                return success;
            },
            deleteProperty(target, prop) {
                const
                    oldValue = target[prop],
                    success  = Reflect.deleteProperty(target, prop);

                if (success) {
                    onChange({
                        oldValue,
                        path    : path.concat(prop),
                        deleted : true
                    });
                }
                return success;
            }
        });

        processed.set(obj, proxy);
        return proxy;
    };

/**
 * Helper for Object manipulation.
 */
export default class ObjectHelper extends Objects {
    static $name = 'ObjectHelper';

    // These methods are inherited from Objects (an internal class) but need to be documented here for public use.
    // This is primarily because static methods, while inherited by JavaScript classes, are not displayed in derived
    // classes in the docs.

    /**
     * Copies all enumerable properties from the supplied source objects to `dest`. Unlike `Object.assign`, this copy
     * also includes inherited properties.
     * @param {Object} dest The destination object.
     * @param {...Object} sources The source objects.
     * @returns {Object} The `dest` object.
     * @method assign
     * @static
     */

    /**
     * Copies all enumerable properties from the supplied source objects to `dest`, only including properties that does
     * not already exist on `dest`. Unlike `Object.assign`, this copy also includes inherited properties.
     * @param {Object} dest The destination object.
     * @param {...Object} sources The source objects.
     * @returns {Object} The `dest` object.
     * @method assignIf
     * @static
     */

    /**
     * Creates a deep copy of the `value`. Simple objects ({@link #function-isObject-static}, arrays and `Date` objects
     * are cloned. The enumerable properties of simple objects and the elements of arrays are cloned recursively.
     * @param {*} value The value to clone.
     * @param {Function} [handler] An optional function to call for values of types other than simple object, array or
     * `Date`. This function should return the clone of the `value` passed to it. It is only called for truthy values
     * whose `typeof` equals `'object'`.
     * @param {*} handler.value The value to clone.
     * @returns {*} The cloned value.
     * @method clone
     * @static
     */

    /**
     * Converts a list of names (either a space separated string or an array), into an object with those properties
     * assigned truthy values. The converse of {@link #function-getTruthyKeys-static}.
     * @param {String|String[]} source The list of names to convert to object form.
     * @method createTruthyKeys
     * @static
     */

    /**
     * Gathers the names of properties which have truthy values into an array.
     *
     * This is useful when gathering CSS class names for complex element production.
     * Instead of appending to an array or string which may already contain the
     * name, and instead of contending with space separation and concatenation
     * and conditional execution, just set the properties of an object:
     *
     *     cls = {
     *         [this.selectedCls] : this.isSelected(thing),
     *         [this.dirtyCls] : this.isDirty(thing)
     *     };
     *
     * If the `source` parameter is an array, it is returned. If it is a single string, it is wrapped in an array and
     * returned. Otherwise, the value must be an object (or null) it will be processed as described above.
     *
     * @param {Object|String|String[]} source Source of keys to gather into an array.
     * @returns {String[]} The keys which had a truthy value.
     * @method getTruthyKeys
     * @static
     */

    /**
     * Gathers the values of properties which are truthy into an array.
     * @param {Object} source Source of values to gather into an array.
     * @returns {String[]} The truthy values from the passed object.
     * @method getTruthyValues
     * @static
     */

    /**
     * Tests whether a passed object has any enumerable properties.
     * @param {Object} object
     * @returns {Boolean} `true` if the passed object has no enumerable properties.
     * @method isEmpty
     * @static
     */

    /**
     * Returns `true` if the `value` is a simple `Object`.
     * @param {Object} value
     * @returns {Boolean} `true` if the `value` is a simple `Object`.
     * @method isObject
     * @static
     */

    /**
     * Copies all enumerable properties from the supplied source objects to `dest`, recursing when the properties of
     * both the source and `dest` are objects.
     * ```
     *  const o = {
     *      a : 1,
     *      b : {
     *          c : 2
     *      }
     *  };
     *  const o2 = {
     *      b : {
     *          d : 3
     *      }
     *  }
     *
     *  console.log(merge(o, o2));
     *
     *  > { a : 1, b : { c : 2, d : 3 } }
     * ```
     * @param {Object} dest The destination object.
     * @param {...Object} sources The source objects.
     * @returns {Object} The `dest` object.
     * @method merge
     * @static
     */

    /**
     * Returns the specific type of the given `value`. Unlike the `typeof` operator, this function returns the text
     * from the `Object.prototype.toString` result allowing `Date`, `Array`, `RegExp`, and others to be differentiated.
     * ```
     *  console.log(typeOf(null));
     *  > null
     *
     *  console.log(typeOf({}));
     *  > object
     *
     *  console.log(typeOf([]));
     *  > array
     *
     *  console.log(typeOf(new Date()));
     *  > date
     *
     *  console.log(typeOf(NaN));
     *  > nan
     *
     *  console.log(typeOf(/a/));
     *  > regexp
     * ```
     * @param {*} value
     * @returns {String}
     * @method typeOf
     * @static
     */

    /**
     * Returns value for a given path in the object
     * @param {Object} object Object to check path on
     * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
     * @returns {*} Value associated with passed key
     * @method getPath
     * @static
     */

    /**
     * Sets value for a given path in the object
     * @param {Object} object Target object
     * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
     * @param {*} value Value for a given path
     * @returns {Object} Returns passed object
     * @method setPath
     * @static
     */

    /**
     * Recursively wraps an object and all nested objects in Proxy instances.
     * Invokes a callback whenever any property is mutated (set or deleted) at any depth.
     *
     * This is useful for observing changes in complex objects, such as configuration objects,
     * where you want to track changes to any property, no matter how deeply nested.
     *
     * It is advisable to debounce the `onChange` callback to avoid performance issues with frequent updates.
     * @param {Object} target - The root object to wrap in Proxies.
     * @param {Function} onChange - A callback function called on any mutation.
     * Receives an object with: path (array of keys), oldValue, newValue, and optional deleted flag.
     * @param {String[]} onChange.path The path to the changed property.
     * @param {Object} onChange.oldValue The previous value of the property.
     * @param {Object} [onChange.newValue] The new value of the property if any.
     * @param {Boolean} [onChange.deleted] Indicates if the property was deleted.
     * @returns {Object} A Proxy which wraps the original object.
     */
    static observeObject(target, onChange) {
        return wrapRecursive(target, onChange, new WeakMap());
    }

    /**
     * Returns an object retaining the enumerable key/value pairs for which the provided function `fn` returns a truthy
     * value.
     *
     * @param {Object|Object[]} object The object or array of objects whose properties are to be filtered. If an array
     * is passed, the key/value pairs of each object is evaluated and copied into the result (based on `fn`).
     * @param {Function} fn The function that returns a truthy if the key/value pair should be in the result.
     * @param {*} fn.value The value of the `object` property `name`.
     * @param {String} fn.name The name of the current property for the iteration.
     * @param {Object} fn.object The `object` being filtered.
     */
    static filter(object, fn) {
        let ret = null,
            key, o, src, value;

        if (object) {
            ret = Object.getPrototypeOf(object) ? {} : Object.create(null);
            src = Array.isArray(object) ? object : [object];

            for (o of src) {
                for (key in o) {
                    if (fn(value = o[key], key, o)) {  // same as [].filter
                        ret[key] = value;
                    }
                }
            }
        }

        return ret;
    }

    /**
     * Calls a provided function `fn` for each key/value pair of a given `object`.
     * @param {Object} object The object to iterate
     * @param {Function} fn The function to call with each property and value of the given `object`.
     * @param {*} fn.value The value of the `object` property `name`.
     * @param {String} fn.name The name of the current property for the iteration.
     */
    static forEach(object, fn) {
        let key;

        if (object) {
            if (Array.isArray(object)) {
                object.forEach(fn);
            }
            else {
                for (key in object) {
                    // pass (value, key) to match Array.forEach
                    fn(object[key], key);
                }
            }
        }
    }

    /**
     * Creates a new object where key is a property in array item (`ref` by default) or index in the array and value is array item.
     *
     * From:
     * ```
     * [
     *     {
     *          text : 'foo',
     *          ref : 'fooItem'
     *     },
     *     {
     *          text : 'bar'
     *     }
     * ]
     * ```
     *
     * To:
     * ```
     * {
     *     fooItem : {
     *         text : 'foo',
     *         ref  : 'fooItem'
     *     },
     *     1 : {
     *         text : 'bar'
     *     }
     * }
     * ```
     *
     * @param {Object[]} arrayOfItems Array to transform.
     * @param {String} [prop] Property to read the key from. `ref` by default.
     * @returns {Object} namedItems
     */
    static transformArrayToNamedObject(arrayOfItems, prop = 'ref') {
        const namedItems = {};

        arrayOfItems.forEach((item, index) => {
            const
                // 0 is valid value, but empty string in not valid
                key = (item[prop] != null && item[prop].toString().length) ? item[prop] : index;

            namedItems[key] = item;
        });

        return namedItems;
    }

    /**
     * Creates a new array from object values and saves key in a property (`ref` by default) of each item.
     *
     * From:
     * ```
     * {
     *     fooItem : {
     *         text : 'foo'
     *     },
     *     1 : {
     *         text : 'bar'
     *     },
     *     barItem : false // will be ignored
     * }
     * ```
     *
     * To:
     * ```
     * [
     *     {
     *          text : 'foo',
     *          ref : 'fooItem'
     *     },
     *     {
     *          text : 'bar',
     *          ref : 1
     *     }
     * ]
     * ```
     *
     * @param {Object} namedItems Object to transform.
     * @param {String} [prop] Property to save the key to. `ref` by default.
     * @returns {Object[]} arrayOfItems
     */
    static transformNamedObjectToArray(namedItems, prop = 'ref') {
        return Object.keys(namedItems).filter(key => namedItems[key]).map(key => {
            const item = namedItems[key];

            item[prop] = key;

            return item;
        });
    }

    /**
     * Checks if two values are equal. Basically === but special handling of dates.
     * @param {*} a First value
     * @param {*} b Second value
     * @param {Boolean} [useIsDeeply=false] Pass `true` to match arrays & objects using a deep equality check.
     * @param {Boolean} [matchNullish=false] Pass `true` to have nullish (`undefined` and `null`) evaluated as equal.
     * @returns {*} true if values are equal, otherwise false
     */
    static isEqual(a, b, useIsDeeply = false, matchNullish = false) {
        if (a === b) {
            return true;
        }

        // Since a !== b, if either is nullish we can end here.
        if (a == null || b == null) {
            // If !matchNullish, the result is false. Otherwise the result is true if both are nullish (a == b covers
            // this since at least one is nullish)

            // noinspection EqualityComparisonWithCoercionJS
            return matchNullish && a == b;
        }

        // Comparing only model data
        if (a.isModel && b.isModel) {
            return a.equals(b);
        }

        if (a.isBase || b.isBase) {
            // Skip comparing object instances (since they aren't === return false)
            return false;
        }

        const array = Array.isArray(a) + Array.isArray(b);   // 0, 1 or 2

        if (array) {
            return array === 2 && a.length === b.length &&
                a.every((v, idx) => OH.isEqual(v, b[idx], useIsDeeply));
        }

        if (a instanceof Date && b instanceof Date) {
            // faster than calling DateHelper.isEqual
            // https://jsbench.me/3jk2bom2r3/1
            return a.getTime() === b.getTime();
        }

        const
            typeA = typeof a,
            typeB = typeof b;

        // since a !== b we won't find type equality for these types (leaving only 'object' of interest):
        //  boolean, bigint, function, number, string, symbol, undefined
        if (typeA === typeB && typeA === 'object') {
            if (a.constructor.prototype === b.constructor.prototype) {
                return useIsDeeply
                    ? OH.isDeeplyEqual(a, b, useIsDeeply)
                    : Helpers.String.safeJsonStringify(a, 'circular') === Helpers.String.safeJsonStringify(b, 'circular');
            }
        }

        return String(a) === String(b);
    }

    /**
     * Checks if two objects are deeply equal
     * @param {Object} a
     * @param {Object} b
     * @param {Object} [options] Additional comparison options
     * @param {Object} [options.ignore] Map of property names to ignore when comparing
     * @param {Function} [options.shouldEvaluate] Function used to evaluate if a property should be compared or not.
     * Return false to prevent comparison
     * @param {Function} [options.evaluate] Function used to evaluate equality. Return `true`/`false` as evaluation
     * result or anything else to let `isEqual` handle the comparison
     * @returns {Boolean}
     */
    static isDeeplyEqual(a, b, options = {}) {
        // Same object, equal :)
        if (a === b) {
            return true;
        }

        // Nothing to compare, not equal
        if (!a || !b) {
            return false;
        }

        // Property names excluding ignored
        const
            aKeys = OH.keys(a, options.ignore),
            bKeys = OH.keys(b, options.ignore);

        // Property count differs, not equal
        if (aKeys.length !== bKeys.length) {
            return false;
        }

        for (let i = 0; i < aKeys.length; i++) {
            const
                aKey = aKeys[i],
                bKey = bKeys[i];

            // Property name differs, not equal
            if (aKey !== bKey) {
                return false;
            }

            const
                aVal = a[aKey],
                bVal = b[bKey];

            // <remove-on-release>
            // TODO: Not currently used
            // </remove-on-release>
            // Allow caller to determine if property values should be evaluated or not
            if (options.shouldEvaluate) {
                if (options.shouldEvaluate(
                    aKey,
                    {
                        value  : aVal,
                        object : a
                    }, {
                        value  : bVal,
                        object : b
                    }
                ) === false) {
                    continue;
                }
            }

            // Allow caller to determine equality of properties
            if (options.evaluate) {
                const result = options.evaluate(aKey, {
                    value  : aVal,
                    object : a
                }, {
                    value  : bVal,
                    object : b
                });

                // Not equal
                if (result === false) {
                    return false;
                }

                // Equal, skip isEqual call below
                if (result === true) {
                    continue;
                }
            }

            // Values differ, not equal (also digs deeper)
            if (!OH.isEqual(aVal, bVal, options)) {
                return false;
            }
        }

        // Found to be equal
        return true;
    }

    /**
     * Checks if value B is partially equal to value A.
     * @param {*} a First value
     * @param {*} b Second value
     * @returns {Boolean} true if values are partially equal, false otherwise
     */
    static isPartial(a, b) {
        a = String(a).toLowerCase();
        b = String(b).toLowerCase();

        return a.indexOf(b) !== -1;
    }

    /**
     * Checks if value a is smaller than value b.
     * @param {*} a First value
     * @param {*} b Second value
     * @returns {Boolean} true if a < b
     */
    static isLessThan(a, b) {
        if (a instanceof Date && b instanceof Date) {
            return Helpers.Date.isBefore(a, b);
        }
        return a < b;
    }

    /**
     * Checks if value a is bigger than value b.
     * @param {*} a First value
     * @param {*} b Second value
     * @returns {Boolean} true if a > b
     */
    static isMoreThan(a, b) {
        if (a instanceof Date && b instanceof Date) {
            return Helpers.Date.isAfter(a, b);
        }
        return a > b;
    }

    /**
     * If `value` is iterable (`for-of` loopable), this method returns the first element of the iterable sequence.
     * If `value` is async-iterable (i.e., uses a `for await of` loop), this method returns a `Promise` that will
     * resolve to the first element.
     * Otherwise, this method returns the first key/value pair (a 2-element array) using a `for-in` loop.
     * @param {*} value
     * @param {*} [defaultValue] The value to return if `value` is empty.
     * @returns {*}
     * @internal
     */
    static first(value, defaultValue) {
        let ret = defaultValue,
            key;

        if (value) {
            if (value[Symbol.iterator]) {
                // noinspection LoopStatementThatDoesntLoopJS
                for (ret of value) {     // eslint-disable-line no-unreachable-loop
                    break;
                }
            }
            else if (value[Symbol.asyncIterator]) {
                ret = firstOfAsync(value, defaultValue);
            }
            else if (ObjectHelper.isObject(value)) {
                if (ret === undefined) {
                    ret = EMPTY_ARRAY;
                }

                // noinspection LoopStatementThatDoesntLoopJS
                for (key in value) {     // eslint-disable-line no-unreachable-loop
                    ret = [key, value[key]];
                    break;
                }
            }
        }

        return ret;
    }

    /**
     * Used by the Base class to make deep copies of defaultConfig blocks
     * @private
     */
    static fork(obj) {
        let ret, key, value;

        if (obj && obj.constructor === Object) {
            ret = Object.create(obj);

            for (key in obj) {
                value = obj[key];

                if (value) {
                    if (value.constructor === Object) {
                        ret[key] = OH.fork(value);
                    }
                    else if (value instanceof Array) {
                        ret[key] = value.slice();
                    }
                }
            }
        }
        else {
            ret = obj;
        }

        return ret;
    }

    /**
     * Copies the named properties from the `source` parameter into the `dest` parameter.
     * @param {Object} dest The destination into which properties are copied.
     * @param {Object} source The source from which properties are copied.
     * @param {String[]} props The list of property names.
     * @returns {Object} The `dest` object.
     */
    static copyProperties(dest, source, props) {
        let prop, i;
        for (i = 0; i < props.length; i++) {
            prop = props[i];
            if (prop in source) {
                dest[prop] = source[prop];
            }
        }
        return dest;
    }

    /**
     * Copies the named properties from the `source` parameter into the `dest` parameter
     * unless the property already exists in the `dest`.
     * @param {Object} dest The destination into which properties are copied.
     * @param {Object} source The source from which properties are copied.
     * @param {String[]} props The list of property names.
     * @returns {Object} The `dest` object.
     */
    static copyPropertiesIf(dest, source, props) {
        if (source) {
            for (const prop of props) {
                if (!(prop in dest) && prop in source) {
                    dest[prop] = source[prop];
                }
            }
        }
        return dest;
    }

    /**
     * Copies all properties from the `source` parameter into the `dest` parameter except those specified in `except`.
     * @param {Object} dest The destination into which properties are copied.
     * @param {Object} source The source from which properties are copied.
     * @param {Object} except Object whose keys specify the properties to exclude.
     * @returns {Object} The `dest` object.
     */
    static copyPropertiesExcept(dest, source, except) {
        if (source) {
            for (const prop in source) {
                if (!except?.[prop]) {
                    dest[prop] = source[prop];
                }
            }
        }
        return dest;
    }

    /**
     * Returns an array containing the keys and values of all enumerable properties from every prototype level for the
     * object. If `object` is `null`, this method returns an empty array.
     * @param {Object} object Object from which to retrieve entries.
     * @param {Object|Function} [ignore] Optional object of names to ignore or a function accepting the name and value
     * which returns `true` to ignore the item.
     * @returns {Array}
     * @internal
     */
    static entries(object, ignore) {
        const
            result = [],
            call = typeof ignore === 'function';

        if (object) {
            for (const p in object) {
                if (call ? !ignore(p, object[p]) : !ignore?.[p]) {
                    result.push([p, object[p]]);
                }
            }
        }

        return result;
    }

    /**
     * Populates an `object` with the provided `entries`.
     * @param {Array} entries The key/value pairs (2-element arrays).
     * @param {Object} [object={}] The object onto which to add `entries`.
     * @returns {Object} The passed `object` (by default, a newly created object).
     * @internal
     */
    static fromEntries(entries, object) {
        object = object || {};

        if (entries) {
            for (let i = 0; i < entries.length; ++i) {
                object[entries[i][0]] = entries[i][1];
            }
        }

        return object;
    }

    /**
     * Returns an array containing all enumerable property names from every prototype level for the object. If `object`
     * is `null`, this method returns an empty array.
     * @param {Object} object Object from which to retrieve property names.
     * @param {Object|Function} [ignore] Optional object of names to ignore or a function accepting the name and value
     * which returns `true` to ignore the item.
     * @param {Function} [mapper] Optional function to call for each non-ignored item. If provided, the result of this
     * function is stored in the returned array. It is called with the array element as the first parameter, and the
     * index in the result array as the second argument (0 for the first, non-ignored element, 1 for the second and so
     * on).
     * @returns {String[]}
     */
    static keys(object, ignore, mapper) {
        const
            result = [],
            call = typeof ignore === 'function';

        if (object) {
            let index = 0;

            for (const p in object) {
                if (call ? !ignore(p, object[p]) : !ignore?.[p]) {
                    result.push(mapper ? mapper(p, index) : p);
                    ++index;
                }
            }
        }

        return result;
    }

    /**
     * Returns an array containing the values of all enumerable properties from every prototype level for the object.
     * If `object` is `null`, this method returns an empty array.
     * @param {Object} object Object from which to retrieve values.
     * @param {Object|Function} [ignore] Optional object of names to ignore or a function accepting the name and value
     * which returns `true` to ignore the item.
     * @param {Function} [mapper] Optional function to call for each non-ignored item. If provided, the result of this
     * function is stored in the returned array. It is called with the array element as the first parameter, and the
     * index in the result array as the second argument (0 for the first, non-ignored element, 1 for the second and so
     * on).
     * @returns {Array}
     * @internal
     */
    static values(object, ignore, mapper) {
        const
            result = [],
            call = typeof ignore === 'function';

        if (object) {
            let index = 0;

            for (const p in object) {
                if (call ? !ignore(p, object[p]) : !ignore?.[p]) {
                    result.push(mapper ? mapper(object[p], index) : object[p]);
                    ++index;
                }
            }
        }

        return result;
    }

    //region Path

    /**
     * Checks if a given path exists in an object
     * @param {Object} object Object to check path on
     * @param {String} path Dot-separated path, e.g. 'object.childObject.someKey'
     * @returns {Boolean} Returns `true` if path exists or `false` if it does not
     */
    static pathExists(object, path) {
        const properties = path.split('.');

        return properties.every(property => {
            if (!object || !(property in object)) {
                return false;
            }
            object = object[property];
            return true;
        });
    }

    /**
     * Creates a simple single level key-value object from complex deep object.
     * @param {Object} object Object to extract path and values from
     * @returns {Object} Key-value object where key is a path to the corresponding value
     * @internal
     *
     * ```javascript
     * // converts deep object
     * {
     *     foo : {
     *         bar : {
     *             test : 1
     *         }
     *     }
     * }
     * // into a single level object
     * {
     *     'foo.bar.test' : 1
     * }
     * ```
     */
    static pathifyKeys(object, fieldDataSourceMap) {
        const result = {};

        for (const key in object) {
            if (hasOwn(object, key)) {
                const field = fieldDataSourceMap?.[key];
                // do not use path keys if `fieldDataSourceMap` is provided (for top level keys)
                const usesPathKeys = field?.type === 'object' || field?.complexMapping || !Boolean(fieldDataSourceMap);

                if (usesPathKeys && Array.isArray(object[key])) {
                    result[key] = object[key].slice();
                }
                else if (usesPathKeys && object[key] instanceof Object) {
                    const paths = this.pathifyKeys(object[key]);

                    for (const path in paths) {
                        result[`${key}.${path}`] = paths[path];
                    }
                }
                else {
                    result[key] = object[key];
                }
            }
        }

        return result;
    }

    /**
     * Removes value for a given path in the object. Doesn't cleanup empty objects.
     * @param {Object} object
     * @param {String} path Dot-separated path, e.g. `obj.child.someKey`
     * @internal
     */
    static deletePath(object, path) {
        path.split('.').reduce((result, key, index, array) => {
            if (result == null) {
                return null;
            }

            if (hasOwn(result, key)) {
                if (index === array.length - 1) {
                    delete result[key];
                }
                else {
                    return result[key];
                }
            }
        }, object);
    }

    //endregion

    static coerce(from, to) {
        const fromType = Objects.typeOf(from),
            toType = Objects.typeOf(to),
            isString = typeof from === 'string';

        if (fromType !== toType) {
            switch (toType) {
                case 'string':
                    return String(from);
                case 'number':
                    return Number(from);
                case 'boolean':
                    // See http://ecma262-5.com/ELS5_HTML.htm#Section_11.9.3 as to why '0'.
                    // TL;DR => ('0' == 0), so if given string '0', we must return boolean false.
                    return isString && (!from || from === 'false' || from === '0') ? false : Boolean(from);
                case 'null':
                    return isString && (!from || from === 'null') ? null : false;
                case 'undefined':
                    return isString && (!from || from === 'undefined') ? undefined : false;
                case 'date':
                    return isString && isNaN(from) ? Helpers.Date.parse(from) : Date(Number(from));
            }
        }
        return from;
    }

    static wrapProperty(object, propertyName, newGetter, newSetter, deep = true) {
        const newProperty = {};

        let proto = Object.getPrototypeOf(object),
            existingProperty = Object.getOwnPropertyDescriptor(proto, propertyName);

        while (!existingProperty && proto && deep) {
            proto = Object.getPrototypeOf(proto);
            if (proto) {
                existingProperty = Object.getOwnPropertyDescriptor(proto, propertyName);
            }
        }

        if (existingProperty) {
            if (existingProperty.set) {
                newProperty.set = v => {
                    existingProperty.set.call(object, v);

                    // Must invoke the getter in case "v" has been transformed.
                    newSetter && newSetter.call(object, existingProperty.get.call(object));
                };
            }
            else {
                newProperty.set = newSetter;
            }
            if (existingProperty.get) {
                newProperty.get = () => {
                    let result = existingProperty.get.call(object);
                    if (newGetter) {
                        result = newGetter.call(object, result);
                    }
                    return result;
                };
            }
            else {
                newProperty.get = newGetter;
            }
        }
        else {
            newProperty.set = v => {
                object[`_${propertyName}`] = v;
                newSetter && newSetter.call(object, v);
            };
            newProperty.get = () => {
                let result = object[`_${propertyName}`];
                if (newGetter) {
                    result = newGetter.call(object, result);
                }
                return result;
            };
        }
        Object.defineProperty(object, propertyName, newProperty);
    }

    /**
     * Intercepts access to a `property` of a given `object`.
     *
     * ```javascript
     *      ObjectHelper.hookProperty(object, 'prop', class {
     *          get value() {
     *              return super.value;
     *          }
     *          set value(v) {
     *              super.value = v;
     *          }
     *      });
     * ```
     * The use of `super` allows the hook's getter and setter to invoke the object's existing get/set.
     *
     * @param {Object} object
     * @param {String} property
     * @param {Function} hook A `class` defining a `value` property getter and/or setter.
     * @returns {Function} A function that removes the hook when called.
     * @internal
     */
    static hookProperty(object, property, hook) {
        const
            desc = ObjectHelper.getPropertyDescriptor(hook.prototype, 'value'),
            existingDesc = ObjectHelper.getPropertyDescriptor(object, property),
            fieldName = `_${property}`,
            base = class {
                get value() {
                    return existingDesc ? existingDesc.get.call(this) : this[fieldName];
                }

                set value(v) {
                    if (existingDesc) {
                        existingDesc.set.call(this, v);
                    }
                    else {
                        this[fieldName] = v;
                    }
                }
            },
            baseDesc = ObjectHelper.getPropertyDescriptor(base.prototype, 'value');

        Object.setPrototypeOf(hook.prototype, base.prototype);  // direct super calls to our "base" implementation
        Object.defineProperty(object, property, {
            configurable : true,

            get : desc.get || baseDesc.get,
            set : desc.set || baseDesc.set
        });

        return () => delete object[property];
    }

    /**
     * Changes the passed object and removes all null and undefined properties from it
     * @param {Object} object Target object
     * @param {Boolean} [keepNull] Pass true to only remove undefined properties
     * @returns {Object} Passed object
     */
    static cleanupProperties(object, keepNull = false) {
        Object.entries(object).forEach(([key, value]) => {
            if (keepNull) {
                value === undefined && delete object[key];
            }
            else {
                value == null && delete object[key];
            }
        });
        return object;
    }

    /**
     * Changes the passed object and removes all properties from it.
     * Used while mutating when need to keep reference to the object but replace its properties.
     * @param {Object} object Target object
     * @returns {Object} Passed object
     */
    static removeAllProperties(obj) {
        Object.keys(obj).forEach(key => delete obj[key]);
        return obj;
    }

    //region Assert type

    /**
     * Checks that the supplied value is of the specified type.Throws if it is not
     * @param {Object} value Value to check type of
     * @param {String} type Expected type
     * @param {String} name Name of the value, used in error message
     * @param {Boolean} [allowNull] Accept `null` without throwing
     */
    static assertType(value, type, name) {
        const valueType = Objects.typeOf(value);

        if (value != null && valueType !== type) {
            throw new Error(`Incorrect type "${valueType}" for ${name}, expected "${type}"`);
        }
    }

    /**
     * Checks that the supplied value is a plain object. Throws if it is not
     * @param {Object} value Value to check type of
     * @param {String} name Name of the value, used in error message
     */
    static assertObject(value, name) {
        OH.assertType(value, 'object', name);
    }

    /**
     * Checks that the supplied value is an instance of a Bryntum class. Throws if it is not
     * @param {Object} value Value to check type of
     * @param {String} name Name of the value, used in error message
     */
    static assertInstance(value, name) {
        OH.assertType(value, 'instance', name);
    }

    /**
     * Checks that the supplied value is a Bryntum class. Throws if it is not
     * @param {Object} value Value to check type of
     * @param {String} name Name of the value, used in error message
     */
    static assertClass(value, name) {
        OH.assertType(value, 'class', name);
    }

    /**
     * Checks that the supplied value is a function. Throws if it is not
     * @param {Object} value Value to check type of
     * @param {String} name Name of the value, used in error message
     */
    static assertFunction(value, name) {
        if (typeof value !== 'function' || value.isBase || value.$$name) {
            throw new Error(`Incorrect type for ${name}, got "${value}" (expected a function)`);
        }
    }

    /**
     * Checks that the supplied value is a number. Throws if it is not
     * @param {Object} value Value to check type of
     * @param {String} name Name of the value, used in error message
     */
    static assertNumber(value, name, positive) {
        const asNumber = Number(value);

        if (typeof value !== 'number' || isNaN(asNumber) || (positive && value < 0)) {
            throw new Error(`Incorrect type for ${name}, got "${value}" (expected a ${positive ? 'positive ' : ''}Number)`);
        }
    }

    /**
     * Checks that the supplied value is a boolean. Throws if it is not
     * @param {Object} value Value to check type of
     * @param {String} name Name of the value, used in error message
     */
    static assertBoolean(value, name) {
        OH.assertType(value, 'boolean', name);
    }

    /**
     * Checks that the supplied value is a string. Throws if it is not
     * @param {Object} value Value to check type of
     * @param {String} name Name of the value, used in error message
     */
    static assertString(value, name) {
        OH.assertType(value, 'string', name);
    }

    /**
     * Checks that the supplied value is an array. Throws if it is not
     * @param {Object} value Value to check type of
     * @param {String} name Name of the value, used in error message
     */
    static assertArray(value, name) {
        OH.assertType(value, 'array', name);
    }

    //endregion

    /**
     * Number.toFixed(), with polyfill for browsers that needs it
     * @param {Number} number
     * @param {Number} digits
     * @returns {String} A fixed point string representation of the passed number.
     */
    static toFixed(number, digits) {
        if (toFixedFix) {
            return toFixedFix(number, digits);
        }

        return number.toFixed(digits);
    }

    /**
     * Round the passed number to closest passed step value.
     * @param {Number} number The number to round.
     * @param {Number} [step] The step value to round to.
     * @returns {Number} The number rounded to the closest step.
     */
    static roundTo(number, step = 1) {
        return Math.round(number / step) * step;
    }

    /**
     * Round the passed number to the passed number of decimals.
     * @param {Number} number The number to round.
     * @param {Number} digits The number of decimal places to round to.
     * @returns {Number} The number rounded to the passed number of decimal places.
     */
    static round(number, digits) {
        // Undefined or null means do not round. NOT round to no decimals.
        if (digits == null) {
            return number;
        }

        const factor = 10 ** digits;

        return Math.round(number * factor) / factor;
    }

    /**
     * Returns a non-null entry from a Map for a given key path. This enables a specified defaultValue to be added "just
     * in time" which is returned if the key is not already present.
     * @param {Map} map The Map to find the key in (and potentially add to).
     * @param {String|Number|String[]|Number[]} path Dot-separated path, e.g. 'firstChild.childObject.someKey',
     * or the key path as an array, e.g. ['firstChild', 'childObject', 'someKey'].
     * @param {Object} [defaultValue] Optionally the value to insert if the key is not found.
     */
    static getMapPath(map, path, defaultValue) {
        const
            keyPath   = Array.isArray(path) ? path : typeof path === 'string' ? path.split('.') : [path],
            simpleKey = keyPath.length === 1,
            topKey    = keyPath[0],
            topValue  = map.has(topKey) ? map.get(topKey) : map.set(topKey, simpleKey ? defaultValue : {}).get(topKey);

        // If it was a simple key, we are done.
        if (simpleKey) {
            return topValue;
        }

        // Go down the property path on the top Object, filling entries in until the leaf.
        return OH.getPathDefault(topValue, keyPath.slice(1), defaultValue);
    }
}

Helpers.register(ObjectHelper);

const OH = ObjectHelper;
